import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
} from "recharts";
import { Typography, Grid, Box } from "@mui/material";
import { useAlert } from "../Alert";
import { useTranslation } from "react-i18next";
import instance from "../../globals/axios";
import { useParams } from "react-router-dom";
import axios from "axios";
import { SERVER_BASE_ADDRESS } from "../../globals/const";

const SoilQuality = () => {
  const [data, setData] = useState(null);
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { access_key } = useParams();
  const isReport = !!access_key;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let response;
        if (isReport) {
          const url = `${SERVER_BASE_ADDRESS}/app-admin/get-soil-quality-stats/`;
          response = await axios.get(url, {
            headers: {
              Authorization: `Token ${access_key}`,
            },
          });
        } else {
          response = await instance.get("/app-admin/get-soil-quality-stats/");
        }

        if (response?.data?.data) {
          setData(response.data.data);
        } else {
          showAlert("error", "Failed to fetch data");
        }
      } catch (error) {
        console.error("Failed to fetch data", error);
        showAlert("error", "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const COLORS = ["#FF0000", "#FF8042", "#008000"];
  const centeredItemStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };

  return (
    <Box sx={{ padding: "5px" }}>
      {!isReport && loading ? (
        <Typography variant="h6">{`${t("Loading")}...`}</Typography>
      ) : data ? (
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            sx={{ mt: 10 }}
            style={centeredItemStyle}
          >
            <Typography variant="subtitle1">{t("Average pH")}</Typography>
            <BarChart width={200} height={300} data={[data?.average_values[0]]}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar
                dataKey="value"
                fill="#8884d8"
                isAnimationActive={!isReport}
              />
            </BarChart>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            sx={{ mt: 10 }}
            style={centeredItemStyle}
          >
            <Typography variant="subtitle1">{t("pH Distribution")}</Typography>
            <PieChart width={350} height={350}>
              <Pie
                data={data.ph_distribution}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={90}
                fill="#8884d8"
                isAnimationActive={!isReport}
              >
                {data.ph_distribution?.map((entry, index) => (
                  <Cell
                    key={`cell-ph-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            style={centeredItemStyle}
            sx={{
              mt: 10,
              mb: {
                sm: 13,
              },
            }}
          >
            <Typography variant="subtitle1">{t("Average SOC")}</Typography>
            <BarChart width={200} height={300} data={[data?.average_values[1]]}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar
                dataKey="value"
                fill="#8884d8"
                isAnimationActive={!isReport}
              />
            </BarChart>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            style={centeredItemStyle}
            sx={{
              mt: 10,
              mb: {
                sm: 13,
              },
            }}
          >
            <Typography variant="subtitle1">{t("SOC Distribution")}</Typography>
            <PieChart width={250} height={300}>
              <Pie
                data={data.soc_distribution}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={90}
                fill="#8884d8"
                isAnimationActive={!isReport}
              >
                {data.soc_distribution?.map((entry, index) => (
                  <Cell
                    key={`cell-soc-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </Grid>
        </Grid>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100px",
          }}
        >
          <Typography variant="h6">{t("No data available")}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default SoilQuality;
